import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useForm } from '@dotone/react-ui-core/hooks';
import { useTranslate, useShallowEqualSelector, useSearchSelector } from '@hooks';
import { createImageCreative, updateImageCreative } from '@actions';
import { Row, Col } from 'reactstrap';
import {
  FormBasic as Form,
  Label,
  Alert,
  FormGroup as BsFormGroup,
  Input,
  AlertPopover,
  FileUploader,
  LocaleCheckInput,
  CardBasic,
  DeepLinkInput,
  ActivePeriodDateTimePicker,
} from '@dotone/react-ui-core';
import { OfferSearchInput, OfferVariantRadioGroup } from '../forms';

const FormGroup = ({ label, required = false, hintProps, ...otherProps }) => (
  <BsFormGroup row>
    <Col md={4} className="text-left text-md-right">
      <Label className="text-sm text-nowrap" required={required}>
        {label}
        {hintProps && <AlertPopover {...hintProps} />}
      </Label>
    </Col>
    <Col md={8} {...otherProps} />
  </BsFormGroup>
);

const ImageCreativeForm = ({ imageCreative = {}, onCancel, ...otherProps }) => {
  const { locale, t } = useTranslate('imageCreative', 'model');
  const { tHint, tHtml } = useTranslate('imageCreative', 'hints');
  const dispatch = useDispatch();

  const newRecord = !imageCreative.id;

  const defaultValues = {
    ...imageCreative,
    ...(newRecord ? { images: [] } : {}),
  };

  const { handleSubmit, connectForm, watch, setValue, setError } = useForm({ defaultValues });

  const [offerId, offerVariantId, images, cdnUrl] = watch([
    'offerId',
    'offerVariantId',
    'images',
    'cdnUrl',
  ]);

  const {
    data = { message: {} },
    submitting,
    isSubmitSuccess,
    status,
  } = useShallowEqualSelector(({ imageCreativeCreate: create, imageCreativeUpdate: update }) => ({
    data: defaultValues.id ? update.data : create.data,
    isSubmitSuccess: create.status === 'success' || update.status === 'success',
    submitting: create.isLoading || update.isLoading,
    status: create.status || update.status,
  }));

  const { data: offers } = useSearchSelector(({ offerSearch }) => offerSearch, {
    ids: [offerId],
    locale,
    active: newRecord,
    forImageCreative: true,
    offerVariants: true,
  });

  // Setup for toggling offer variant selection
  const selectedOffer = offerId && offers.find((offer) => String(offer.id) === String(offerId));

  const handleSubmitForm = ({ id, ...values }) => {
    if (id) {
      dispatch(updateImageCreative(id, values));
    } else {
      dispatch(createImageCreative(values));
    }
  };

  const handleImageUpload = (values) => {
    if (newRecord) {
      setValue('images', values);
    } else {
      setValue('cdnUrl', values.cdnUrl);
      setValue('width', values.width);
      setValue('height', values.height);
    }
  };

  useEffect(() => {
    if (!submitting && isSubmitSuccess) {
      onCancel();
    }
  }, [submitting, onCancel, isSubmitSuccess]);

  useEffect(() => {
    if (status === 'error' && data.message) {
      Object.keys(data.message).forEach((key) =>
        setError(key, { type: 'custom', message: data.message[key] })
      );
    }
  }, [data, setError, status]);

  return (
    <Form
      forModal
      onSubmit={handleSubmit(handleSubmitForm)}
      onCancel={onCancel}
      submitting={submitting}
      submitLabel={t('imageCreative:actions.submit')}
      {...otherProps}
    >
      {connectForm(
        <>
          <CardBasic className="border border-light" title={t('form.landingPageTitle')}>
            <FormGroup label={t('common:model.offer')} hintProps={tHint('offer')} required>
              <OfferSearchInput
                loadInitial
                forImageCreative
                name="offerId"
                active={newRecord}
                searchParams={{ offerVariants: true }}
                required
              />
            </FormGroup>
            {!!selectedOffer?.offerVariants?.length && (
              <FormGroup
                label={t('common:model.offerVariant')}
                hintProps={tHint('offerVariant')}
                required
              >
                <OfferVariantRadioGroup
                  name="offerVariantId"
                  offerVariants={selectedOffer.offerVariants}
                  rules={{ required: true }}
                />
              </FormGroup>
            )}
            {selectedOffer?.deeplinkable && (
              <>
                <Row>
                  <Col md={{ size: 8, offset: 4 }} lg={{ size: 8, offset: 4 }}>
                    <Alert variant="warning">
                      <div>
                        {tHtml('clientUrl', { url: selectedOffer.destinationDomains.join(', ') })}
                      </div>
                    </Alert>
                  </Col>
                </Row>
                <FormGroup label={t('clientUrl')}>
                  <DeepLinkInput
                    name="clientUrl"
                    domain={selectedOffer.destinationDomains}
                    placeholder={t('form.clientUrlPlaceholder')}
                  />
                </FormGroup>
              </>
            )}
            <FormGroup label={t('locales')} hintProps={tHint('locale')} required>
              <LocaleCheckInput name="locales" rules={{ required: true }} />
            </FormGroup>
          </CardBasic>
          <CardBasic className="border border-light" title={t('form.campaignPeriodTitle')}>
            <ActivePeriodDateTimePicker
              disablePastDate
              hintProps={tHint('activePeriod')}
              names={{
                start: 'activeDateStart',
                end: 'activeDateEnd',
                infinity: 'isInfinityTime',
              }}
            />
          </CardBasic>
          <CardBasic className="border border-light" title={t('form.imageUploadTitle')}>
            <FileUploader
              image
              entity="imageCreative"
              maxSize={204800}
              onSuccess={handleImageUpload}
              previewLinks={newRecord ? images.map((image) => image.cdnUrl) : cdnUrl}
              maxFiles={newRecord ? 10 : 1}
              disabled={!offerId || !offerVariantId}
            />
            <Input
              type="hidden"
              name={newRecord ? 'images' : 'cdnUrl'}
              rules={{ required: true }}
            />
          </CardBasic>
        </>
      )}
    </Form>
  );
};

export default ImageCreativeForm;
